/**
 * IXD vs Workbook 구조 비교 페이지 전역 상태 스토어
 * 페이지 스코프로 동작하며, Provider 언마운트 시 상태가 초기화됩니다.
 */

import { createContext, useContext } from "react";
import { createStore, useStore } from "zustand";
import { IxdWorkbookCompareResponse } from "@/apis/ixd-workbook-compare/route";

export type IxdWorkbookCompareState = {
  ixdFile: File | null;
  selectedWorkbookId: string | null;
  result: IxdWorkbookCompareResponse | null;
  isCompareView: boolean;
  /** 사이드바에서 선택된 목차(노트) id */
  activeNoteId: string | null;
  setIxdFile: (file: File | null) => void;
  setSelectedWorkbookId: (workbookId: string | null) => void;
  setResult: (result: IxdWorkbookCompareResponse) => void;
  setActiveNoteId: (id: string | null) => void;
  backToUpload: () => void;
  reset: () => void;
};

export const createIxdWorkbookCompareStore = () =>
  createStore<IxdWorkbookCompareState>()((set) => ({
    ixdFile: null,
    selectedWorkbookId: null,
    result: null,
    isCompareView: false,
    activeNoteId: null,
    setIxdFile: (ixdFile) => set({ ixdFile }),
    setSelectedWorkbookId: (selectedWorkbookId) => set({ selectedWorkbookId }),
    setResult: (result) =>
      set({ result, isCompareView: true, activeNoteId: null }),
    setActiveNoteId: (activeNoteId) => set({ activeNoteId }),
    backToUpload: () => set({ isCompareView: false }),
    reset: () =>
      set({
        ixdFile: null,
        selectedWorkbookId: null,
        result: null,
        isCompareView: false,
        activeNoteId: null,
      }),
  }));

export type IxdWorkbookCompareStore = ReturnType<
  typeof createIxdWorkbookCompareStore
>;

export const IxdWorkbookCompareStoreContext =
  createContext<IxdWorkbookCompareStore | null>(null);

/** 페이지 내 전역 상태 구독. Provider 밖에서 사용 시 에러를 던집니다. */
export const useIxdWorkbookCompareStore = <T,>(
  selector: (state: IxdWorkbookCompareState) => T,
): T => {
  const store = useContext(IxdWorkbookCompareStoreContext);
  if (!store) {
    throw new Error(
      "useIxdWorkbookCompareStore must be used within IxdWorkbookCompareStoreProvider",
    );
  }
  return useStore(store, selector);
};
